// Living style guide — the canonical type scale, colors, and components, rendered
// in the real fonts. Internal reference (behind the gate, not in the nav).
//
// The one font rule: SANS (Jost) for running body copy, captions, and inputs;
// SERIF (Cormorant Garamond) for everything else — titles, labels, dates, nav,
// buttons, links. Ink is full pine; pine/60 for captions, pine/40 for faint.
// Tracking collapses to 0.2em (labels/buttons/nav), 0.15em (dates/times),
// 0.02em (body). No rounded corners.

type Row = { label: string; cls: string; sample: string };

const EYEBROW =
  "text-sm font-semibold uppercase tracking-[0.2em] text-midnight-teal";

function Spec({ title, rows }: { title: string; rows: Row[] }) {
  return (
    <section className="border-t border-pine/15 py-10">
      <p className={`mb-6 ${EYEBROW}`}>{title}</p>
      <div className="space-y-7">
        {rows.map((r) => (
          <div
            key={r.label}
            className="grid gap-2 md:grid-cols-[1fr_auto] md:items-baseline md:gap-8"
          >
            <div className={r.cls}>{r.sample}</div>
            <div className="font-sans text-xs text-pine/50 md:text-right">
              <span className="font-semibold text-pine/70">{r.label}</span>
              <code className="mt-1 block">{r.cls}</code>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

const SWATCHES = [
  { name: "pine", hex: "#0a3323", on: "text-cream" },
  { name: "midnight-teal", hex: "#006981", on: "text-cream" },
  { name: "dusty-teal", hex: "#a2ccdb", on: "text-pine" },
  { name: "sage-moss", hex: "#839958", on: "text-cream" },
  { name: "dusty-rose", hex: "#d38c97", on: "text-pine" },
  { name: "blush", hex: "#e2a9ab", on: "text-pine" },
  { name: "cream", hex: "#f2efe6", on: "text-pine" },
];

export function StyleGuide() {
  return (
    <div className="mx-auto max-w-3xl pb-24">
      <header className="py-10">
        <p className={EYEBROW}>Internal reference</p>
        <h1 className="mt-3 font-serif text-5xl text-pine sm:text-6xl">
          Style guide
        </h1>
        <p className="mt-4 font-sans text-lg font-normal tracking-[0.02em] text-pine">
          One rule: <strong className="font-semibold">serif</strong> (Cormorant
          Garamond) is the site&rsquo;s voice for all display and labels —
          titles, section heads, eyebrows, dates, times, nav, buttons.{" "}
          <strong className="font-semibold">Sans</strong> (Jost) is reserved for
          running body copy and helper text, where it reads more easily. Ink is
          full pine. Dates and times stay serif small-caps by choice. When in
          doubt, match this page.
        </p>
      </header>

      <Spec
        title="Display — serif"
        rows={[
          {
            label: "Page title (H1)",
            cls: "font-serif text-5xl text-pine sm:text-6xl",
            sample: "The Estate at River Run",
          },
          {
            label: "Section (H2)",
            cls: "font-serif text-4xl text-pine md:text-5xl",
            sample: "Hindu Ceremony",
          },
          {
            label: "Subhead (H3)",
            cls: "font-serif text-2xl text-pine md:text-3xl",
            sample: "Indian or western formal",
          },
        ]}
      />

      <Spec
        title="Body — sans (Jost)"
        rows={[
          {
            label: "Body",
            cls: "font-sans text-lg font-normal tracking-[0.02em] text-pine",
            sample:
              "Running copy is Jost at full pine — readable, warm, and identical on every page.",
          },
          {
            label: "Caption / helper",
            cls: "font-sans text-base text-pine/60",
            sample: "Secondary and helper text sits a step back at pine/60.",
          },
          {
            label: "Faint / meta",
            cls: "font-sans text-sm text-pine/40",
            sample: "Timestamps, counts, and incidental meta.",
          },
        ]}
      />

      <Spec
        title="Labels, nav, dates & times — serif"
        rows={[
          {
            label: "Eyebrow label",
            cls: "text-sm font-semibold uppercase tracking-[0.2em] text-midnight-teal",
            sample: "Dress Code",
          },
          {
            label: "Nav",
            cls: "font-serif text-base font-semibold uppercase tracking-widest text-pine",
            sample: "Schedule",
          },
          {
            label: "Date",
            cls: "font-serif text-xl font-semibold uppercase tracking-[0.15em] text-midnight-teal md:text-2xl",
            sample: "Saturday, May 1st, 2027",
          },
          {
            label: "Time",
            cls: "font-serif text-2xl font-semibold uppercase tracking-[0.15em] text-pine md:text-3xl",
            sample: "10:30 AM",
          },
        ]}
      />

      <section className="border-t border-pine/15 py-10">
        <p className={`mb-6 ${EYEBROW}`}>Buttons — serif, squared</p>
        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            className="bg-midnight-teal px-10 py-3 text-base font-medium uppercase tracking-[0.2em] text-cream transition-colors hover:bg-pine"
          >
            Primary
          </button>
          <button
            type="button"
            className="border border-midnight-teal px-10 py-3 text-base font-medium uppercase tracking-[0.2em] text-midnight-teal transition-colors hover:bg-midnight-teal hover:text-cream"
          >
            Secondary
          </button>
          <button
            type="button"
            className="text-sm uppercase tracking-[0.2em] text-midnight-teal transition-colors hover:text-pine"
          >
            Text button
          </button>
        </div>
        <p className="mt-4 font-sans text-sm text-pine/50">
          Squared corners — no pills. One tracking (0.2em) across labels, CTAs,
          and text buttons.
        </p>
      </section>

      <section className="border-t border-pine/15 py-10">
        <p className={`mb-6 ${EYEBROW}`}>Palette</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {SWATCHES.map((s) => (
            <div
              key={s.name}
              className={`flex h-24 flex-col justify-end p-3 ${s.on}`}
              style={{ backgroundColor: s.hex }}
            >
              <span className="font-sans text-sm font-semibold">{s.name}</span>
              <span className="font-sans text-xs opacity-80">{s.hex}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
